import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["mapContainer", "mapWrapper"];
  static values = {
    apiKey: String,
    results: Array,
    startingPoint: Array,
    routingEnabled: { type: Boolean, default: true },
    enabled: Boolean,
  };

  connect() {
    // Initialize venue data storage for navigation
    this.venueData = new Map();
    this.currentPopup = null;
    this.currentRoutingProfile = "walking"; // Default routing profile
    this.currentRouteDestination = null; // Track current route destination
    this.routeRequestId = 0;

    if (!window.mapboxgl) {
      console.error("Mapbox GL JS n'est pas chargé !");
      return;
    }

    if (!this.resultsValue || this.resultsValue.length === 0) {
      console.warn("Aucun résultat à afficher sur la map.");
      return;
    }

    window.mapboxgl.accessToken = this.apiKeyValue;

    const center = this.#initialCenter();

    this.map = new window.mapboxgl.Map({
      container: this.mapContainerTarget,
      style: "mapbox://styles/mapbox/streets-v11",
      center: center,
      zoom: 12,
    });

    this.map.on("load", () => {
      this.#addMarkersToMap();
      this.#fitMapToBounds();
      if (this.routingEnabledValue) {
        this.#addTransportSelector();
      }
    });
  }

  enlargeYourMap() {
    this.mapWrapperTarget.classList.add("large");
    this.map.resize();
  }

  shrinkYourMap() {
    this.mapWrapperTarget.classList.remove("large");
    this.map.resize();
  }

  preventDefault(e) {
    e.stopPropagation();
  }

  activateMarker(e) {
    const venueId = e.target.dataset.venueId ?? 0;
    const targetMarker = this.#getMarkerId(venueId);

    if (targetMarker) {
      targetMarker.dataset.hoverActive = "true";
      targetMarker.dataset.activeBeforeHover = targetMarker.classList.contains(
        "active",
      )
        ? "true"
        : "false";
      targetMarker.classList.add("active");

      if (this.enabledValue) {
        this.#fitMapToSelected(venueId);
      }

      if (this.routingEnabledValue) {
        // Show route on hover
        const latitude = parseFloat(targetMarker.dataset.latitude);
        const longitude = parseFloat(targetMarker.dataset.longitude);
        this.currentRouteDestination = { longitude, latitude };
        this.#drawRoute(longitude, latitude);
      }
    }
  }

  displayVenueBox(e) {
    // Find the marker element (might be the target or a parent)
    const markerElement = e.target.closest(".locator-icon");

    // Remove active class from all markers
    document.querySelectorAll(".locator-icon").forEach((marker) => {
      marker.classList.remove("active");
      delete marker.dataset.hoverActive;
      delete marker.dataset.activeBeforeHover;
    });

    // Add active class to clicked marker
    markerElement.classList.add("active");

    const latitude = parseFloat(markerElement.dataset.latitude);
    const longitude = parseFloat(markerElement.dataset.longitude);

    if (this.routingEnabledValue) {
      this.currentRouteDestination = { longitude, latitude };
      this.#drawRoute(longitude, latitude);
    }
  }

  #initialCenter() {
    if (this.routingEnabledValue && this.hasStartingPointValue) {
      return [this.startingPointValue[1], this.startingPointValue[0]];
    }

    const firstResult = this.resultsValue[0];
    return [firstResult.longitude, firstResult.latitude];
  }

  #getMarkerId(venueId) {
    return Array.from(document.querySelectorAll(".locator-icon")).find(
      (marker) => marker.id === `marker-${venueId}`,
    );
  }

  prevActivity(e) {
    this.#navigateActivity(e, -1);
  }

  nextActivity(e) {
    this.#navigateActivity(e, 1);
  }

  #navigateActivity(e, direction) {
    const popupElement = e.target.closest(".venue-popup");
    const venueId = parseInt(popupElement.dataset.venueId);
    const currentIndex = parseInt(popupElement.dataset.currentActivity);
    const newIndex = currentIndex + direction;

    const venueGroup = this.venueData.get(venueId);

    if (newIndex >= 0 && newIndex < venueGroup.club_activities.length) {
      // Update popup content
      const newContent = this.#createPopupContent(venueGroup, newIndex);
      popupElement.outerHTML = newContent;
    }
  }

  async #drawRoute(destinationLng, destinationLat) {
    if (!this.routingEnabledValue || !this.hasStartingPointValue) return;

    const startLng = this.startingPointValue[1];
    const startLat = this.startingPointValue[0];
    const routeRequestId = ++this.routeRequestId;

    this.#removeRouteLayers();

    try {
      // Get directions from Mapbox Directions API using current profile
      const query = await fetch(
        `https://api.mapbox.com/directions/v5/mapbox/${this.currentRoutingProfile}/${startLng},${startLat};${destinationLng},${destinationLat}?steps=true&geometries=geojson&access_token=${this.apiKeyValue}`,
        { method: "GET" },
      );

      const json = await query.json();
      if (routeRequestId !== this.routeRequestId) return;

      const data = json.routes[0];
      const route = data.geometry.coordinates;

      // Add route to map
      this.map.addSource("route", {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {},
          geometry: {
            type: "LineString",
            coordinates: route,
          },
        },
      });

      this.map.addLayer({
        id: "route",
        type: "line",
        source: "route",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#9565FE",
          "line-width": 3,
          "line-dasharray": [2, 3],
        },
      });

      const walkingTimeMinutes = Math.round(data.duration / 60);
      const distance = Math.round(data.distance);

      // Show time in the travel time box instead of on the route
      this.#showTravelTime(walkingTimeMinutes, distance);
    } catch (error) {
      if (routeRequestId !== this.routeRequestId) return;

      // Fallback to straight line if routing fails
      this.#drawStraightLine(
        startLng,
        startLat,
        destinationLng,
        destinationLat,
      );
    }
  }

  #drawStraightLine(startLng, startLat, destinationLng, destinationLat) {
    this.map.addSource("route", {
      type: "geojson",
      data: {
        type: "Feature",
        properties: {},
        geometry: {
          type: "LineString",
          coordinates: [
            [parseFloat(startLng), parseFloat(startLat)],
            [destinationLng, destinationLat],
          ],
        },
      },
    });

    this.map.addLayer({
      id: "route",
      type: "line",
      source: "route",
      layout: {
        "line-join": "round",
        "line-cap": "round",
      },
      paint: {
        "line-color": "#9565FE",
        "line-width": 3,
        "line-dasharray": [2, 3],
      },
    });
  }

  #clearRoute() {
    this.routeRequestId += 1;
    this.#removeRouteLayers();

    // Hide travel time box
    this.#hideTravelTime();
  }

  #removeRouteLayers() {
    if (this.map.getLayer("route")) {
      this.map.removeLayer("route");
    }
    if (this.map.getSource("route")) {
      this.map.removeSource("route");
    }
    if (this.map.getLayer("walking-time-label")) {
      this.map.removeLayer("walking-time-label");
    }
    if (this.map.getSource("walking-time-label")) {
      this.map.removeSource("walking-time-label");
    }
  }

  #showTravelTime(timeMinutes, distance) {
    // Get or create travel time display
    let travelTimeBox =
      this.mapContainerTarget.querySelector(".travel-time-box");

    if (!travelTimeBox) {
      travelTimeBox = document.createElement("div");
      travelTimeBox.className = "travel-time-box hidden";
      this.mapContainerTarget.appendChild(travelTimeBox);
    }

    // Update content (only show time, no distance)
    travelTimeBox.innerHTML = `${timeMinutes} min`;

    travelTimeBox.classList.remove("hidden");
  }

  #hideTravelTime() {
    const travelTimeBox =
      this.mapContainerTarget.querySelector(".travel-time-box");
    if (travelTimeBox) {
      travelTimeBox.classList.add("hidden");
    }
  }

  #ensurePopupVisible(longitude, latitude, anchor) {
    if (!this.routingEnabledValue || !this.hasStartingPointValue) return;

    // Wait a frame for popup to be rendered
    setTimeout(() => {
      const markerPixel = this.map.project([
        parseFloat(longitude),
        parseFloat(latitude),
      ]);
      const mapContainer = this.map.getContainer();
      const mapWidth = mapContainer.offsetWidth;
      const mapHeight = mapContainer.offsetHeight;

      // Try to get actual popup dimensions, fallback to estimates
      const popupElement = document.querySelector(".mapboxgl-popup-content");
      let popupWidth = 360; // Default fallback
      let popupHeight = 230; // Default fallback

      if (popupElement) {
        const rect = popupElement.getBoundingClientRect();
        popupWidth = rect.width;
        popupHeight = rect.height;
      }

      const offset = 35; // Our offset value
      const margin = 20; // Safety margin

      // Calculate required space for popup
      let requiredLeft = markerPixel.x - popupWidth / 2 - margin;
      let requiredRight = markerPixel.x + popupWidth / 2 + margin;
      let requiredTop, requiredBottom;

      if (anchor === "bottom") {
        requiredTop = markerPixel.y - popupHeight - offset - margin;
        requiredBottom = markerPixel.y + margin;
      } else {
        // anchor === 'top'
        requiredTop = markerPixel.y - margin;
        requiredBottom = markerPixel.y + popupHeight + offset + margin;
      }

      // Check if departure point would be visible after potential pan
      const departurePixel = this.map.project([
        this.startingPointValue[1],
        this.startingPointValue[0],
      ]);
      const panX =
        requiredLeft < 0
          ? requiredLeft
          : requiredRight > mapWidth
            ? requiredRight - mapWidth
            : 0;
      const panY =
        requiredTop < 0
          ? requiredTop
          : requiredBottom > mapHeight
            ? requiredBottom - mapHeight
            : 0;

      const departureFinalX = departurePixel.x - panX;
      const departureFinalY = departurePixel.y - panY;

      // If departure would be outside viewport after pan, or popup still doesn't fit, use fitBounds
      if (
        departureFinalX < 0 ||
        departureFinalX > mapWidth ||
        departureFinalY < 0 ||
        departureFinalY > mapHeight ||
        requiredRight - requiredLeft > mapWidth ||
        requiredBottom - requiredTop > mapHeight
      ) {
        // Create bounds that include both points + popup space
        const newBounds = new mapboxgl.LngLatBounds();
        newBounds.extend([
          this.startingPointValue[1],
          this.startingPointValue[0],
        ]); // departure
        newBounds.extend([parseFloat(longitude), parseFloat(latitude)]); // destination

        // Add extra padding to account for popup size
        const popupPadding = Math.max(popupWidth / 2, popupHeight / 2) + 50;

        this.map.fitBounds(newBounds, {
          padding: popupPadding,
          duration: 300,
        });
      } else if (panX !== 0 || panY !== 0) {
        // Simple pan is sufficient
        this.map.panBy([panX, panY], { duration: 300 });
      }
    }, 50);
  }

  #createPopupContent(venueGroup, currentActivityIndex = 0) {
    const hasMultipleActivities = venueGroup.club_activities.length > 1;
    const currentClubActivity =
      venueGroup.club_activities[currentActivityIndex];
    const activityDots = venueGroup.club_activities
      .map(
        (_activity, index) => `
          <span
            class="block h-2 w-2 rounded-full transition-all duration-200 ease-in-out ${index === currentActivityIndex ? "bg-grey" : "bg-grey-light"}"
            aria-hidden="true"
          ></span>
        `,
      )
      .join("");

    return `
      <div class="venue-popup" data-venue-id="${venueGroup.venue_id}" data-current-activity="${currentActivityIndex}">
        <div class="popup-header">
          <h3><a href="/club_activities/${currentClubActivity.club_activity_id}" class="club-link">${currentClubActivity.club_name}</a></h3>
          ${
            hasMultipleActivities
              ? `
            <div class="navigation-chevrons">
              <button class="prev-activity" data-action="click->multimap#prevActivity" ${currentActivityIndex === 0 ? "disabled" : ""}>
                <img src="/icons/chevron-left.svg" alt="Previous" />
              </button>
              <button class="next-activity" data-action="click->multimap#nextActivity" ${currentActivityIndex === venueGroup.club_activities.length - 1 ? "disabled" : ""}>
                <img src="/icons/chevron-right.svg" alt="Next" />
              </button>
            </div>
          `
              : ""
          }
        </div>

        <div class="activities-tags">
          <span class="activity-tag">${currentClubActivity.activity_name}</span>
        </div>

        ${
          hasMultipleActivities
            ? `
          <div class="flex justify-center gap-2 mb-4" aria-label="Résultat ${currentActivityIndex + 1} sur ${venueGroup.club_activities.length}">
            ${activityDots}
          </div>
        `
            : ""
        }

        <div class="venue-name">
          ${venueGroup.venue_name}
        </div>

        <div class="venue-address">
          ${venueGroup.full_address}
        </div>

        ${
          venueGroup.formatted_distance
            ? `
          <div class="venue-distance">
            ${venueGroup.formatted_distance}
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  #calculateDistance(lat, lng) {
    const startLat = this.startingPointValue[0];
    const startLng = this.startingPointValue[1];

    // Simple distance calculation (Haversine formula)
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (startLat * Math.PI) / 180;
    const φ2 = (lat * Math.PI) / 180;
    const Δφ = ((lat - startLat) * Math.PI) / 180;
    const Δλ = ((lng - startLng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distance = R * c; // Distance in meters

    return distance < 1000
      ? `${Math.round(distance)}m`
      : `${(distance / 1000).toFixed(1)}km`;
  }

  #addMarkersToMap() {
    if (this.routingEnabledValue && this.hasStartingPointValue) {
      const spLong = this.startingPointValue[1];
      const spLat = this.startingPointValue[0];

      // Create custom departure marker
      const departureEl = document.createElement("div");
      departureEl.style.width = "49px";
      departureEl.style.height = "55px";
      departureEl.style.cursor = "pointer";

      // Load SVG content for departure marker
      fetch("/icons/departure-locator.svg")
        .then((response) => response.text())
        .then((svgContent) => {
          departureEl.innerHTML = svgContent;
        });

      // Calculate anchor position based on marker position in viewport
      const markerPixel = this.map.project([
        parseFloat(spLong),
        parseFloat(spLat),
      ]);
      const mapHeight = this.map.getContainer().offsetHeight;
      const anchor = markerPixel.y < mapHeight / 2 ? "bottom" : "top";

      const departurePopup = new mapboxgl.Popup({
        closeButton: false,
        offset: 35,
        anchor: anchor,
      }).setHTML('<div class="departure-popup">Départ</div>');

      // Add popup visibility check for departure popup too
      departurePopup.on("open", () => {
        this.#ensurePopupVisible(spLong, spLat, anchor);
      });

      new mapboxgl.Marker({ element: departureEl })
        .setLngLat([parseFloat(spLong), parseFloat(spLat)])
        .setPopup(departurePopup)
        .addTo(this.map);
    }

    // Results are now pre-grouped by venue
    this.resultsValue.forEach((venueGroup) => {
      const latitude = venueGroup.latitude;
      const longitude = venueGroup.longitude;
      const name = venueGroup.venue_name;
      const iconPath = `/icons/locator-${venueGroup.results_count}.svg`;

      // Store venue data for navigation
      this.venueData.set(venueGroup.venue_id, venueGroup);

      const el = document.createElement("div");
      el.id = `marker-${venueGroup.venue_id}`;
      el.style.width = "35px";
      el.style.height = "42px";
      el.classList.add("locator-icon");
      el.style.cursor = "pointer";
      el.setAttribute("data-action", "click->multimap#displayVenueBox");
      el.setAttribute("data-latitude", latitude);
      el.setAttribute("data-longitude", longitude);

      // Load SVG content and embed it inline
      fetch(iconPath)
        .then((response) => response.text())
        .then((svgContent) => {
          el.innerHTML = svgContent;
        });

      // Calculate anchor position based on marker position in viewport
      const markerPixel = this.map.project([
        parseFloat(longitude),
        parseFloat(latitude),
      ]);
      const mapHeight = this.map.getContainer().offsetHeight;
      const anchor = markerPixel.y < mapHeight / 2 ? "bottom" : "top";

      const popup = new mapboxgl.Popup({
        closeButton: false,
        offset: 35,
        anchor: anchor,
      }).setHTML(this.#createPopupContent(venueGroup, 0));

      // Track popup when it opens
      popup.on("open", () => {
        this.currentPopup = popup;
        // Adjust map bounds to ensure popup is visible
        this.#ensurePopupVisible(longitude, latitude, anchor);
      });

      // Remove active class when popup closes
      popup.on("close", () => {
        if (el.classList.contains("active")) {
          el.classList.remove("active");
          if (this.routingEnabledValue) {
            // Clear route when marker becomes inactive
            this.#clearRoute();
            this.currentRouteDestination = null;
          }
        }
        this.currentPopup = null;
      });

      new mapboxgl.Marker({ element: el })
        .setLngLat([parseFloat(longitude), parseFloat(latitude)])
        .setPopup(popup)
        .addTo(this.map);
    });
  }

  #fitMapToBounds() {
    const bounds = new mapboxgl.LngLatBounds();

    if (this.routingEnabledValue && this.hasStartingPointValue) {
      // Include starting point in bounds calculation
      const spLong = this.startingPointValue[1];
      const spLat = this.startingPointValue[0];
      bounds.extend([parseFloat(spLong), parseFloat(spLat)]);
    }

    // Include at least 8 results marker
    // Including all was too overwhelming
    // If we have less than 8, it will include all
    this.resultsValue.slice(0, 8).forEach(({ latitude, longitude }) => {
      bounds.extend([parseFloat(longitude), parseFloat(latitude)]);
    });

    this.map.fitBounds(bounds, { padding: 50 });
  }

  #fitMapToSelected(id) {
    const bounds = new mapboxgl.LngLatBounds();
    const venue = this.resultsValue.find(
      (r) => r.venue_id === Number.parseInt(id),
    );

    const startingPoint = {
      latitude: this.startingPointValue[0],
      longitude: this.startingPointValue[1],
    };

    const ary = [startingPoint, venue];

    ary.forEach(({ latitude, longitude }) => {
      bounds.extend([parseFloat(longitude), parseFloat(latitude)]);
    });
    this.map.fitBounds(bounds, { padding: 50 });
  }

  #addTransportSelector() {
    const transportSelector = document.createElement("div");
    transportSelector.className = "transport-selector";

    // Create walking button
    const walkingButton = document.createElement("button");
    walkingButton.className = "transport-mode active";
    walkingButton.dataset.mode = "walking";
    walkingButton.dataset.action = "click->multimap#switchTransportMode";
    walkingButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5.83333 19.1667L8.16667 7.41667L6.66667 8V10.8333H5V6.91667L9.20833 5.125C9.40278 5.04167 9.60764 4.99306 9.82292 4.97917C10.0382 4.96528 10.2431 4.99306 10.4375 5.0625C10.6319 5.13194 10.816 5.22917 10.9896 5.35417C11.1632 5.47917 11.3056 5.63889 11.4167 5.83333L12.25 7.16667C12.6111 7.75 13.1007 8.22917 13.7187 8.60417C14.3368 8.97917 15.0417 9.16667 15.8333 9.16667V10.8333C14.8611 10.8333 13.9931 10.6319 13.2292 10.2292C12.4653 9.82639 11.8125 9.3125 11.2708 8.6875L10.75 11.25L12.5 12.9167V19.1667H10.8333V13.75L9.08333 12.4167L7.58333 19.1667H5.83333ZM11.25 4.58333C10.7917 4.58333 10.3993 4.42014 10.0729 4.09375C9.74653 3.76736 9.58333 3.375 9.58333 2.91667C9.58333 2.45833 9.74653 2.06597 10.0729 1.73958C10.3993 1.41319 10.7917 1.25 11.25 1.25C11.7083 1.25 12.1007 1.41319 12.4271 1.73958C12.7535 2.06597 12.9167 2.45833 12.9167 2.91667C12.9167 3.375 12.7535 3.76736 12.4271 4.09375C12.1007 4.42014 11.7083 4.58333 11.25 4.58333Z" fill="currentColor"/>
      </svg>
    `;

    // Create cycling button
    const cyclingButton = document.createElement("button");
    cyclingButton.className = "transport-mode";
    cyclingButton.dataset.mode = "cycling";
    cyclingButton.dataset.action = "click->multimap#switchTransportMode";
    cyclingButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.16667 18.3333C3.01389 18.3333 2.03125 17.9271 1.21875 17.1146C0.40625 16.3021 0 15.3194 0 14.1667C0 13.0139 0.40625 12.0312 1.21875 11.2188C2.03125 10.4062 3.01389 10 4.16667 10C5.31944 10 6.30208 10.4062 7.11458 11.2188C7.92708 12.0312 8.33333 13.0139 8.33333 14.1667C8.33333 15.3194 7.92708 16.3021 7.11458 17.1146C6.30208 17.9271 5.31944 18.3333 4.16667 18.3333ZM4.16667 16.6667C4.86111 16.6667 5.45139 16.4236 5.9375 15.9375C6.42361 15.4514 6.66667 14.8611 6.66667 14.1667C6.66667 13.4722 6.42361 12.8819 5.9375 12.3958C5.45139 11.9097 4.86111 11.6667 4.16667 11.6667C3.47222 11.6667 2.88194 11.9097 2.39583 12.3958C1.90972 12.8819 1.66667 13.4722 1.66667 14.1667C1.66667 14.8611 1.90972 15.4514 2.39583 15.9375C2.88194 16.4236 3.47222 16.6667 4.16667 16.6667ZM9.16667 15.8333V11.6667L6.5 9.33333C6.33333 9.18056 6.20833 9.00347 6.125 8.80208C6.04167 8.60069 6 8.38889 6 8.16667C6 7.94444 6.04514 7.73264 6.13542 7.53125C6.22569 7.32986 6.34722 7.15278 6.5 7L8.83333 4.66667C9 4.5 9.19097 4.375 9.40625 4.29167C9.62153 4.20833 9.84722 4.16667 10.0833 4.16667C10.3194 4.16667 10.5451 4.20833 10.7604 4.29167C10.9757 4.375 11.1667 4.5 11.3333 4.66667L12.9167 6.25C13.3056 6.63889 13.75 6.94444 14.25 7.16667C14.75 7.38889 15.2778 7.5 15.8333 7.5V9.16667C15.0417 9.16667 14.2882 9.01389 13.5729 8.70833C12.8576 8.40278 12.2222 7.97222 11.6667 7.41667L11 6.75L9 8.75L10.8333 10.6667V15.8333H9.16667ZM12.9167 4.58333C12.4583 4.58333 12.066 4.42014 11.7396 4.09375C11.4132 3.76736 11.25 3.375 11.25 2.91667C11.25 2.45833 11.4132 2.06597 11.7396 1.73958C12.066 1.41319 12.4583 1.25 12.9167 1.25C13.375 1.25 13.7674 1.41319 14.0938 1.73958C14.4201 2.06597 14.5833 2.45833 14.5833 2.91667C14.5833 3.375 14.4201 3.76736 14.0938 4.09375C13.7674 4.42014 13.375 4.58333 12.9167 4.58333ZM15.8333 18.3333C14.6806 18.3333 13.6979 17.9271 12.8854 17.1146C12.0729 16.3021 11.6667 15.3194 11.6667 14.1667C11.6667 13.0139 12.0729 12.0312 12.8854 11.2188C13.6979 10.4062 14.6806 10 15.8333 10C16.9861 10 17.9688 10.4062 18.7812 11.2188C19.5938 12.0312 20 13.0139 20 14.1667C20 15.3194 19.5938 16.3021 18.7812 17.1146C17.9688 17.9271 16.9861 18.3333 15.8333 18.3333ZM15.8333 16.6667C16.5278 16.6667 17.1181 16.4236 17.6042 15.9375C18.0903 15.4514 18.3333 14.8611 18.3333 14.1667C18.3333 13.4722 18.0903 12.8819 17.6042 12.3958C17.1181 11.9097 16.5278 11.6667 15.8333 11.6667C15.1389 11.6667 14.5486 11.9097 14.0625 12.3958C13.5764 12.8819 13.3333 13.4722 13.3333 14.1667C13.3333 14.8611 13.5764 15.4514 14.0625 15.9375C14.5486 16.4236 15.1389 16.6667 15.8333 16.6667Z" fill="currentColor"/>
      </svg>
    `;

    // Create driving button
    const drivingButton = document.createElement("button");
    drivingButton.className = "transport-mode";
    drivingButton.dataset.mode = "driving";
    drivingButton.dataset.action = "click->multimap#switchTransportMode";
    drivingButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 15.8334V16.6667C5 16.9029 4.92014 17.1008 4.76042 17.2605C4.60069 17.4202 4.40278 17.5001 4.16667 17.5001H3.33333C3.09722 17.5001 2.89931 17.4202 2.73958 17.2605C2.57986 17.1008 2.5 16.9029 2.5 16.6667V10.0001L4.25 5.00008C4.33333 4.75008 4.48264 4.54869 4.69792 4.39591C4.91319 4.24314 5.15278 4.16675 5.41667 4.16675H14.5833C14.8472 4.16675 15.0868 4.24314 15.3021 4.39591C15.5174 4.54869 15.6667 4.75008 15.75 5.00008L17.5 10.0001V16.6667C17.5 16.9029 17.4201 17.1008 17.2604 17.2605C17.1007 17.4202 16.9028 17.5001 16.6667 17.5001H15.8333C15.5972 17.5001 15.3993 17.4202 15.2396 17.2605C15.0799 17.1008 15 16.9029 15 16.6667V15.8334H5ZM4.83333 8.33341H15.1667L14.2917 5.83341H5.70833L4.83333 8.33341ZM6.25 13.3334C6.59722 13.3334 6.89236 13.2119 7.13542 12.9688C7.37847 12.7258 7.5 12.4306 7.5 12.0834C7.5 11.7362 7.37847 11.4411 7.13542 11.198C6.89236 10.9549 6.59722 10.8334 6.25 10.8334C5.90278 10.8334 5.60764 10.9549 5.36458 11.198C5.12153 11.4411 5 11.7362 5 12.0834C5 12.4306 5.12153 12.7258 5.36458 12.9688C5.60764 13.2119 5.90278 13.3334 6.25 13.3334ZM13.75 13.3334C14.0972 13.3334 14.3924 13.2119 14.6354 12.9688C14.8785 12.7258 15 12.4306 15 12.0834C15 11.7362 14.8785 11.4411 14.6354 11.198C14.3924 10.9549 14.0972 10.8334 13.75 10.8334C13.4028 10.8334 13.1076 10.9549 12.8646 11.198C12.6215 11.4411 12.5 11.7362 12.5 12.0834C12.5 12.4306 12.6215 12.7258 12.8646 12.9688C13.1076 13.2119 13.4028 13.3334 13.75 13.3334Z" fill="currentColor"/>
      </svg>
    `;

    transportSelector.appendChild(walkingButton);
    transportSelector.appendChild(cyclingButton);
    transportSelector.appendChild(drivingButton);

    this.mapContainerTarget.appendChild(transportSelector);
  }

  switchTransportMode(e) {
    const button = e.target.closest(".transport-mode");
    const newMode = button.dataset.mode;

    // Update active state in selector
    this.mapContainerTarget
      .querySelectorAll(".transport-mode")
      .forEach((btn) => {
        btn.classList.toggle("active", btn.dataset.mode === newMode);
      });

    // Update current profile
    this.currentRoutingProfile = newMode;

    // Redraw route if one is currently displayed
    if (this.currentRouteDestination) {
      this.#drawRoute(
        this.currentRouteDestination.longitude,
        this.currentRouteDestination.latitude,
      );
    }

    // Also redraw if there's an open popup (persistent route)
    const openPopup = document.querySelector(".mapboxgl-popup");
    if (openPopup) {
      const popupContent = openPopup.querySelector(".venue-popup");
      if (popupContent) {
        const latitude = parseFloat(popupContent.dataset.venueId);
        // Find the venue data to get coordinates
        const venueId = parseInt(popupContent.dataset.venueId);
        const venueGroup = this.venueData.get(venueId);
        if (venueGroup) {
          this.#drawRoute(venueGroup.longitude, venueGroup.latitude);
        }
      }
    }
  }
}
